import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { TransformRuleViewModel } from 'src/app/viewModels/transformRuleViewModel';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog, MatDialogConfig  } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { PreviewTableComponent, IPreviewTableDelegate, ColumnInfo } from '../../../preview-table/preview-table.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ConfigService } from 'src/app/services/config/config.service';
import { ConfigViewModelService } from 'src/app/services/configViewModel/config-view-model.service';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { DataTransformerConfig } from 'src/app/domainObjects/dataTransformConfig';
import { DataConnectorMapping } from 'src/app/domainObjects/dataConnectorMapping';
import { DataFarmViewModel } from 'src/app/viewModels/dataFarmViewModel';
import { ImportDataViewModel } from 'src/app/viewModels/importDataViewModel';
import { NavigationService } from '../../../../services/navigation/navigation.service';
import { TransformPipelineService } from 'src/app/services/transformPipeline/transform-pipeline.service';
import { BaseTransformerUIComponent } from 'src/app/views/dataTransformConfig/transformers/base/base-transformer-ui/base-transformer-ui.component';
import { DataDictionaryService } from 'src/app/services/dataDictionary/data-dictionary.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { FormBuilder, FormGroup, UntypedFormControl } from '@angular/forms';
import { SortInfo } from 'src/app/domainObjects/sortInfo';
import { SortBuilderComponent } from 'src/app/views/transform/sort-builder/sort-builder.component';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { UserDataService } from 'src/app/services/user-data-service';
import { RefineryPresenceService } from 'src/app/services/refinery-presence.service';
import { ComponentCanDeactivate } from 'src/app/guards/pending-changes.guard';
import { TransformType, TransformTypeUtil } from 'src/app/domainObjects/transformType';
import { FileDownloadService } from 'src/app/services/file-download.service';
import { ParameterSetting } from 'src/app/domainObjects/masterConfig';
import { TransformerTopHeaderComponent } from '../../transformer-top-header/transformer-top-header.component';
import { ColoredNumberFindReplaceItem, ColoredTextFindReplaceItem, ColoredTextItem, NamedColorList } from 'src/app/domainObjects/coloredTextItem';
import { collectDistinctSourceFields } from 'src/app/views/shared/utils/source-field.util';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { InputPrvwFilterViewModel } from 'src/app/viewModels/inputPrvwFilterViewModel';
import { ColumnCleanerViewModel, RuleType, RuleTypeUtil } from './columnCleanerViewModel';
import { AndOr, AndOrGroup, AndOrUtil, ColumnCleanerRuleViewModel, Criteria, CriteriaUtil, CriteriaUtilSymbol, DuplicateKeep, DuplicateKeepUtil, DuplicateType, DuplicateTypeUtil, FormatAs, FormatAsUtil, KeepRow, KeepRowUtil, MatchValueEntryService, MatchValueEntryType, Prepositions, PrepositionsUtil, RemoveOption, RemoveOptionUtil } from './columnCleanerRuleViewModel';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { ColumnCleanerRule } from 'src/app/domainObjects/transformers/columnCleanerTransform';
import { ValueEntryFieldComponent } from 'src/app/views/shared/value-entry-field/value-entry-field.component';
import { RangeEntryFieldComponent } from 'src/app/views/shared/range-entry-field/range-entry-field.component';
import { TextEntryFieldComponent } from 'src/app/views/shared/text-entry-field/text-entry-field.component';
import { FindReplaceChooserMapDialogOptionComponent } from 'src/app/views/shared/find-replace-chooser-map-dialog-option/find-replace-chooser-map-dialog-option.component';
import { OptionItem, OptionItemDataType } from 'src/app/model/optionItem';
import { Separator, SeparatorNone, SeparatorNoneUtil, SeparatorUtil } from 'src/app/domainObjects/separator';
import { SaveCurrntTransfrmcacheRequest } from 'src/app/messaging/saveCurrntTransfrmcacheRequest';
import { DataFarmTypeUtil } from 'src/app/domainObjects/dataFarmType';

interface GroupHeader {
  isGroup: boolean;
  isSubHeader: boolean;
  ruleTypeIndexValue:number;
  ruleTypeIndex:string;
  actions: string;
  index: string;
  name: string;
  sourcefields:string;
  criteria: string;
  matchvalues: string;
  ifmatched: string;
  logicalOperator:string;
  matchCase: string;
  isEnabled: string;
  rowsDeleted: string;
  rowsRemaining: string;
  valuesModified: string;
  groupHits: string;
  //hits: string;
}

@Component({
    selector: 'app-column-cleaner-transform-detail',
    templateUrl: './column-cleaner-transform-detail.component.html',
    styleUrls: ['./column-cleaner-transform-detail.component.css'],
    standalone: false
})
export class ColumnCleanerTransformDetailComponent extends BaseTransformerUIComponent<ColumnCleanerViewModel>
  implements OnInit, IPreviewTableDelegate,ComponentCanDeactivate {

    /** Column Cleaner has no Default Source Field — pre-select the first rule's first field,
     * falling back to columnCleanerFallbackSourceField when no rule has any field set yet. */
    get columnCleanerDefaultSourceField(): string {
      const fromRule = this.viewModel?.rules?.[0]?.fields?.[0]?.text;
      return fromRule ?? this.columnCleanerFallbackSourceField;
    }

    /** First real (non-"row") column in farm-native order. Used only when no rule has any
     * field set yet — Smart Sample needs at least one field to run against, and with zero
     * rules there is nothing else to offer. Never blended in once real rule fields exist —
     * the Architect, 2026-09-26. */
    private get columnCleanerFallbackSourceField(): string {
      const natural = this.viewModel?.sourceColumnsNaturalOrder ?? [];
      const rowIndex = natural.indexOf('row');
      return (rowIndex >= 0 ? natural[rowIndex + 1] : natural[0]) ?? '';
    }

    /** Distinct fields referenced across all rules, first-rule field first — Smart Sample
     * dropdown. Falls back to columnCleanerFallbackSourceField (alone, not blended in) only
     * when no rule has any field set yet. */
    get columnCleanerAvailableSourceFields(): string[] {
      const fromRules = collectDistinctSourceFields(
        this.viewModel?.rules, r => r.fields?.map((f: ColoredTextItem) => f.text),
      );
      if (fromRules.length > 0) {
        const defaultField = this.columnCleanerDefaultSourceField;
        return [defaultField, ...fromRules.filter(f => f !== defaultField)];
      }
      const fallback = this.columnCleanerFallbackSourceField;
      return fallback ? [fallback] : [];
    }

    RuleType=RuleType;
    RuleTypeUtil = RuleTypeUtil;
    RuleTypes = [
      RuleType.deleteRows,
      RuleType.deDuplicate,
      RuleType.findReplace,
      RuleType.findReplaceAdv,
      RuleType.deleteColumns,
      RuleType.combineColumns,
      RuleType.splitColumns,
      RuleType.moveColumns,
      RuleType.formatColumns,
      RuleType.splitintowords,
      RuleType.removeChars
    ];
    Criteria=Criteria;
    CriteriaUtil = CriteriaUtil;
    Criterias = [
      Criteria.empty,
      Criteria.matches,
      Criteria.contains,
      Criteria.startsWith,
      Criteria.endsWith,
      Criteria.boolTrue,
      Criteria.boolFalse,
      Criteria.equals,
      //Criteria.lessOrGreaterThan,
      //Criteria.lessThan,
      //Criteria.greaterThan,
      //Criteria.LessThanOrEqualTo,
      //Criteria.greaterThanOrEqualTo,
      Criteria.between,
      //Criteria.inclusiveLeft,
      //Criteria.inclusiveRight,
      //Criteria.inclusiveBoth,
      Criteria.equalToday,
      Criteria.lastnDays,
      Criteria.length,
      Criteria.columnMatch
    ];
    CriteriasAdv = [
      Criteria.none,
      Criteria.matches,
      Criteria.contains,
      Criteria.notContains,
      Criteria.startsWith,
      Criteria.endsWith,
      Criteria.boolTrue,
      Criteria.boolFalse,
      Criteria.equals,
      // Criteria.lessThan,
      // Criteria.greaterThan,
      // Criteria.LessThanOrEqualTo,
      // Criteria.greaterThanOrEqualTo,
      Criteria.between,
      // Criteria.inclusiveLeft,
      // Criteria.inclusiveRight,
      // Criteria.inclusiveBoth,
      Criteria.equalToday,
      Criteria.lastnDays,
      Criteria.length
    ];
    CriteriaUtilSymbol=CriteriaUtilSymbol;   
    KeepRow=KeepRow;
    KeepRowUtil = KeepRowUtil;
    KeepRows = [
      KeepRow.first,
      KeepRow.last,
      KeepRow.noRows
    ];
    FormatAs=FormatAs;
    FormatAsUtil = FormatAsUtil;
    FormatAss = [
      FormatAs.title,
      FormatAs.upper,
      FormatAs.lower,
      FormatAs.leftChars,
      FormatAs.rightChars,
      FormatAs.midChars
    ];
    Separator=Separator;
    SeparatorUtil = SeparatorUtil;
    Separators = [
      Separator.pipe,
      Separator.colon,
      Separator.semicolon,
      Separator.comma,
      Separator.dot,
      Separator.space,
      Separator.slash,
      Separator.dash,
      Separator.underscore
    ];

    SeparatorNone=SeparatorNone;
    SeparatorNoneUtil = SeparatorNoneUtil;
    SeparatorNones = [
      SeparatorNone.none,
      SeparatorNone.pipe,
      SeparatorNone.colon,
      SeparatorNone.semicolon,
      SeparatorNone.comma,
      SeparatorNone.commaSpace,
      SeparatorNone.dot,
      SeparatorNone.space,
      SeparatorNone.slash,
      SeparatorNone.dash,
      SeparatorNone.underscore
    ];
    SeparatorWithoutOutNones = [
      SeparatorNone.pipe,
      SeparatorNone.colon,
      SeparatorNone.semicolon,
      SeparatorNone.comma,
      SeparatorNone.commaSpace,
      SeparatorNone.dot,
      SeparatorNone.space,
      SeparatorNone.slash,
      SeparatorNone.dash,
      SeparatorNone.underscore
    ];

    AndOr=AndOr;
    AndOrUtil = AndOrUtil;
    AndOrsA = [
      AndOr.orOr,
      AndOr.andOr,
      AndOr.orAnd,
      AndOr.andAnd,
      AndOr.pairedOr,
      AndOr.pairedAnd
    ];

    AndOrsB = [
      AndOr.orOr,
      AndOr.andOr,
      AndOr.pairedOr,
      AndOr.pairedAnd
    ];

    DuplicateType=DuplicateType;
    DuplicateTypeUtil = DuplicateTypeUtil;
    DuplicateTypes = [
      DuplicateType.exclude,
      DuplicateType.include,
    ];

    DuplicateKeep=DuplicateKeep;
    DuplicateKeepUtil = DuplicateKeepUtil;
    DuplicateKeeps = [
      DuplicateKeep.first,
      DuplicateKeep.last,
      DuplicateKeep.noRows
    ];

    Prepositions=Prepositions;
    PrepositionsUtil = PrepositionsUtil;
    Prepositionss = [
      Prepositions.nochange,
      Prepositions.remove,
      Prepositions.lowercase,
    ];

    RemoveOption=RemoveOption;
    RemoveOptionUtil = RemoveOptionUtil;
    RemoveOptions = [
      RemoveOption.smartClean,
      RemoveOption.removeAll,
      RemoveOption.leadingTrailing,
      RemoveOption.pattern
    ];
  //dataSource: MatTableDataSource<ColumnCleanerRuleViewModel> | undefined;
  dataSource: any;
  dataSourceRule: (ColumnCleanerRuleViewModel | GroupHeader)[] = []; 
  displayedColumns: string[] = [
    'ruleTypeIndex',
    'actions',
    'index',
    'name',
    'sourcefields',
    'additional1',
    'criteria',
    'matchvalues',
    'additional2',
    'ifmatched',
    'logicalOperator',
    'matchCase',
    'isEnabled',
    'rowsDeleted',
    'rowsRemaining',
    'valuesModified',
    'groupHits',
    // 'sampleHits',
    //'hits'
  ];

  previewDataViewModel = new ImportDataViewModel();
  inputPrvwFilterViewModel = new InputPrvwFilterViewModel();
  isRulesMaximized = false;
  isRuleSetEmpty = true;
  NamedColorList=NamedColorList;
  sortCtrl = new UntypedFormControl('');

  form: FormGroup;

  // Explicitly define the type of `spans`
  spans: { [key: number]: { [key: string]: number } } = {};
  separators = [{ value: '0', text: '| (pipe)' },
    { value: '1', text: ': (colon)' },
    { value: '2', text: '; (semicolon) ' },
    { value: '3', text: ', (comma)' },
    { value: '4', text: '" " (space)' },
    { value: '5', text: '/ Forward Slash' },
    { value: '6', text: '\\ Backward Slash' }
  ];
  options: string[] =[...this.separators.map(col => col.text)];

  sourceColumnsString: OptionItem[]=[];
  targetColumnsString: OptionItem[]=[];
  
  constructor(
      activeRoute: ActivatedRoute,
      errorService: ErrorReportingService,
      matDialog: MatDialog,
      snackBar: MatSnackBar,
      router: Router,
      configViewModelService: ConfigViewModelService,
      configService: ConfigService,
      navService: NavigationService,
      transformPipelineService: TransformPipelineService,
      userData: UserDataService,
      dataDictService: DataDictionaryService,
      refineryPresence: RefineryPresenceService,
      private fileDownloadService: FileDownloadService,
      private cdRef: ChangeDetectorRef,private fb: FormBuilder,
      private matchValueEntryService: MatchValueEntryService) {

      super(activeRoute, errorService, matDialog, snackBar, router, configViewModelService, configService, navService, transformPipelineService, userData,dataDictService, refineryPresence);
      this.transformType = TransformType.columnCleaner;
    }
    selectedIndex: number = 0;
    isResultsPanelExpanded = false;
    isPreviewPanelExpanded = false;
    transformerInputPreviewCacheKey = "";
    //other parameters
    subTransformType: ParameterSetting;
    //advanced settings parameters
    threads: ParameterSetting=new ParameterSetting();
    resetRows: ParameterSetting=new ParameterSetting();
    findWildCard: ParameterSetting=new ParameterSetting();
  
    @ViewChild(TransformerTopHeaderComponent) topHeaderComponent: TransformerTopHeaderComponent;
    transformType:TransformType;
    ngOnInit(): void {
      super.ngOnInit();
      //no solid validation in parameter component
      this.isParameterFormValid=true;

      this.userData.setInputTabClicked(false);
      this.userData.setResultsTabClicked(false);
      this.userData.setOutputTabClicked(false);
      this.resetInputPrvwViewModel();
      this.transformerInputPreviewCacheKey = "";
      //validation
      this.form = this.fb.group({
        threads: ['', []],
        subtransformer: [''],
        findWildCard: ['', []],
      });
      this.registerFormGroup(this.form);
  
      //this.totalRulesColumnDisplay();
    }

    protected override async onRouteActivated(_routeParams: any): Promise<void> {
      this.viewModel = new ColumnCleanerViewModel(
        this.dataDictService,
        this.configService,
        this.dataTransformConfigViewModel,
        this.transformViewModel, this.matchValueEntryService);
      this.viewModel.farmType=this.userData.getSelectedNodeDetails().farmtypeid;
      this.configureTargetColumnsFarmtypeid(this.userData.getSelectedNodeDetails().farmtypeid);
      this.snapshotPreState();

      this.dataSource = new MatTableDataSource<ColumnCleanerRuleViewModel>([]);
      this.prepareRuleData();
      this.isRuleSetEmpty = this.viewModel.rules.length === 0;

      if (this.previewTable) { this.previewTable.clear(); }
      if (this.outputDataFarmPreviewTable) { this.outputDataFarmPreviewTable.clear(); }

      await this.setTransformerDetails(this.transformType);
      await this.downloadInputDataAsync();
      this.viewModel.parameters.sourceColumns = this.viewModel.sourceColumns;
      this.viewModel.parameters.targetColumns=this.viewModel.targetColumns;
      await this.setDefaultValues(this.transformType);
      await this.removeInvalidValues();
      this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>this.getDataType(col.pydataType)=="string").forEach(map=>{
        this.sourceColumnsString.push(new OptionItem(map.name,map.name));
      });
      this.sourceColumnsString.sort((a, b) => a.text.localeCompare(b.text));
      var optionitemdatatype=await this.getDataDictOptionItemDataType(this.userData.getSelectedNodeDetails().farmtypeid);
      optionitemdatatype.filter(optitem=>optitem.dataType=="string").forEach(opt=>{
        this.targetColumnsString.push(new OptionItem(opt.text,opt.text));
      });
      this.targetColumnsString.sort((a, b) => a.text.localeCompare(b.text));
    }
  
    prepareRuleData() {
      let currentIndex: number | null = null;
      this.dataSourceRule=[];
      this.viewModel.rules.sort((a,b)=>(a.ruleTypeIndex-b.ruleTypeIndex)).forEach((item) => {
        if (currentIndex !== item.ruleTypeIndex) {
          var actions=RuleTypeUtil.toString(item.ruleType);
          var ruleTypeIndexValue=item.ruleTypeIndex
          if(item.ruleType==RuleType.deleteRows){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"",criteria:"Criteria",matchvalues:"Match value(s)",additional2:"",ifmatched:"If matched ",logicalOperator:"AND/OR",matchCase:"Match Case?",isEnabled:"Run?",rowsDeleted:"Deleted",rowsRemaining:"Remaining",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.deDuplicate){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"",criteria:"Type",matchvalues:"Keep",additional2:"",ifmatched:"",logicalOperator:"",matchCase:"Match Case?",isEnabled:"Run?",rowsDeleted:"Deleted",rowsRemaining:"Remaining",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.findReplace){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: true,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"",criteria:"Find > Replace",matchvalues:"",additional2:"",ifmatched:"",logicalOperator:"",matchCase:"Match All",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.findReplaceAdv){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: false, isSubHeader: true,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"Find > Replace",criteria:"Match All",matchvalues:"Field(s)",additional2:"Criteria",ifmatched:"Match value(s)",logicalOperator:"AND/OR",matchCase:"Match Case?",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"Find > Replace",criteria:"Match All",matchvalues:"Field(s)",additional2:"Criteria",ifmatched:"Match value(s)",logicalOperator:"AND/OR",matchCase:"Match Case?",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.deleteColumns){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"",criteria:"Delete when empty",matchvalues:"",additional2:"",ifmatched:"",logicalOperator:"",matchCase:"",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.combineColumns){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Fields",additional1:"",criteria:"Separator",matchvalues:"Target Field",additional2:"",ifmatched:"Empty field value",logicalOperator:"Remove Chars",matchCase:"Keep orig<br>column(s)",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.splitColumns){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field",additional1:"",criteria:"Separator",matchvalues:"Max Columns",additional2:"",ifmatched:"",logicalOperator:"Remove Chars",matchCase:"Keep orig<br>column",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.moveColumns){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s) > Target Field(s)",additional1:"",criteria:"",matchvalues:"",additional2:"",ifmatched:"",logicalOperator:"Overwrite Data",matchCase:"Keep orig<br>column(s)",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.formatColumns){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field(s)",additional1:"",criteria:"Format As",matchvalues:"No of Chars",additional2:"",ifmatched:"Starting Pos",logicalOperator:"",matchCase:"",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.splitintowords){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field",additional1:"",criteria:"Target Field",matchvalues:"Articles",additional2:"Conjunctions",ifmatched:"Determiners",logicalOperator:"Prepositions",matchCase:"Keep orig<br>column",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }else if(item.ruleType==RuleType.removeChars){
            this.dataSourceRule.push({isHeader: true,ruleType:item.ruleType, isGroup: true, isSubHeader: false,ruleTypeIndexValue:ruleTypeIndexValue,ruleTypeIndex:"ruleTypeIndex",actions:actions, index: "Rule", name: "Rule Name",sourcefields:"Source Field",additional1:"",criteria:"Target Field",matchvalues:"Remove Char",additional2:"",ifmatched:"Remove Type",logicalOperator:"",matchCase:"Keep orig<br>column",isEnabled:"Run?",rowsDeleted:"",rowsRemaining:"",valuesModified:"Modified",groupHits:"Variants" } as GroupHeader);
          }
          
          currentIndex = item.ruleTypeIndex;
        }
        this.dataSourceRule.push(item);
      });
      this.dataSource.data =this.dataSourceRule;
      //this.cacheSpan('ruleTypeIndex', d => d.ruleTypeIndex);
      //update rule table height
      if(this.dataSource.data.length<=10){
        this.minrowcount=this.dataSource.data.length;
      }else{
        this.minrowcount=10;
      }
      this.maxrowcount=this.dataSource.data.length;
    }
    /** Determines whether the row is a group header */
    isGroup(index: number, row: ColumnCleanerRuleViewModel | GroupHeader): boolean {
      return (row as GroupHeader).isGroup === true;
    }
    isSubHeader(index: number, row: ColumnCleanerRuleViewModel | GroupHeader): boolean {
      return (row as GroupHeader).isSubHeader === true;
    }
    onTransformTextChanged(){
      this.viewModel.transformViewModel.name=this.viewModel.transform.name;
      var currenttransform=this.dataTransformConfigViewModel.transforms.find(x=>x.guid==this.viewModel.transform.guid);
      currenttransform.name=this.viewModel.transform.name;
      currenttransform.transform.name=this.viewModel.transform.name;
      var vartransform=this.transformslists.find(x=>x.guid==this.viewModel.transform.guid);
      vartransform.name=this.viewModel.transform.name;
    }
    dropTable(event: CdkDragDrop<MatTableDataSource<ColumnCleanerRuleViewModel>>): void {
      const draggedItem = event.item.data; // Get the dragged item's data
      var prevIndex = event.previousIndex;
      var currentIndex = event.currentIndex;
      const prevRuleType = this.dataSource.data[prevIndex].ruleType;
      const currentRuleType = this.dataSource.data[currentIndex].ruleType;
      
      if (draggedItem.type === 'rule1') {
        // Prevent movement between different ruleType groups
        const previsGroup = this.dataSource.data[prevIndex].isHeader;
        const currentisGroup = this.dataSource.data[currentIndex].isHeader;
        if (previsGroup !== currentisGroup) {
          this.displayWarning("Can't move rules between Subtransform Section","Invalid move!");
          return;
        }
        const subtransformerGroup = this.dataSource.data.filter(sub => sub.ruleTypeIndex === draggedItem.data.ruleTypeIndexValue);
        if(prevIndex>currentIndex){
          moveItemInArray(this.dataSource.data, prevIndex, currentIndex);        
          subtransformerGroup.forEach(sub=>{
            prevIndex++;
            currentIndex++;
            moveItemInArray(this.dataSource.data, prevIndex, currentIndex);
          });
        }else{
          const subitem = this.dataSource.data.filter(sub => sub.ruleTypeIndex === this.dataSource.data[currentIndex].ruleTypeIndexValue);
          var newcurrentIndex=currentIndex+subitem.length;
          moveItemInArray(this.dataSource.data, prevIndex, newcurrentIndex);
          subtransformerGroup.forEach(sub=>{
            prevIndex;
            newcurrentIndex;
            moveItemInArray(this.dataSource.data, prevIndex, newcurrentIndex);
          });
        }
        var h=0;
        var i=0;
        this.dataSource.data.forEach(item=>{
          if(item.isHeader){
            h++;
            item.ruleTypeIndexValue=h;
          }else{
            i++;
            item.index=i;
            item.ruleTypeIndex=h;
          }
        });
        var subtransformer=this.dataSource.data.filter(sub=>!sub.isHeader);
        this.viewModel.rules=subtransformer;
      } 
      else if (draggedItem.type === 'rule2') {
        // Prevent movement between different ruleType groups
        if (prevRuleType !== currentRuleType) {
          this.displayWarning("Can't move rules between Subtransform Section","Invalid move!");
          return;
        }
        moveItemInArray(this.dataSource.data, prevIndex, currentIndex);
        var subtransformer=this.dataSource.data.filter(sub=>!sub.isHeader);
        for (let i = 0; i < subtransformer.length; i++) {
          subtransformer[i].index = i + 1;
        }
        this.viewModel.rules=subtransformer;
      }
      // const currentItem: ColumnCleanerRuleViewModel = event.item.data;
      // currentItem.index = prevIndex;
      //this.viewModel.reindexRules();
      //this.dataSource.data = this.viewModel.rules;
      this.prepareRuleData();
      this.cdRef.detectChanges();
    }
  
    onMatchCaseChanged(event: MatSlideToggleChange): void {
      this.viewModel.rules.forEach( r => r.matchCase = event.checked);
    }

    onRunChanged(event: MatSlideToggleChange): void {
      this.viewModel.rules.forEach( r => r.run = event.checked);
    }

    toggleRulesHeight(): void {
      this.isRulesMaximized = !this.isRulesMaximized;
    }
  
    compareDataFarm(o1: DataFarmViewModel, o2: DataFarmViewModel): boolean {
      return o1.name === o2.name && o1.id === o2.id;
    }
  
    removeSortInfo(sortInfo: SortInfo): void {
      const index = this.viewModel.sortInfo?.indexOf(sortInfo);
  
      if (index >= 0) {
        this.viewModel.sortInfo?.splice(index, 1);
      }
    }
    
    showSortBuilder(): void {
      const dialog = this.matDialog.open(SortBuilderComponent, {
        data: {
          fields: this.viewModel.sourceColumns.map(c => c.text),
          sortItems: [...this.viewModel.sortInfo ?? []] // make copy of array so it doesn't edit actual list
        }
      });
  
      dialog.afterClosed().subscribe(result => {
        if (!result) {
          return;
        }
  
        const items = result.sortItems;
        this.viewModel.sortInfo = items;
        //this.viewModel.sortInfoDisplay = this.viewModel.sortInfo.map(i => `${i.sortField} (${i.sortAscending ? 'asc' : 'desc'})}`).join(',');
        this.sortCtrl.setValue(null);
      });
    }
  
    onDeleteRule(rule: ColumnCleanerRuleViewModel): void {
      this.showConfirmDialog('Delete Confirmation', `Are you sure you want to delete this rule?`, (confirmed) => {
        if (confirmed) {
          const index = this.viewModel.rules.indexOf(rule);
          this.viewModel.rules.splice(index, 1);
          this.viewModel.reindexRules();
  
          if ( this.viewModel.rules.length > 0 ) {
            const lastIndex = this.viewModel.rules.length - 1;
            //this.viewModel.selectedIngestConfig = this.viewModel.importDataViewModel.ingestConfigs[lastIndex];
          }
          else {
            //this.viewModel.selectedIngestConfig = null;
          }
          //this.dataSource.data = this.viewModel.rules;
          this.isRuleSetEmpty = this.viewModel.rules.length === 0;

          this.prepareRuleData();
          this.cdRef.detectChanges();
        }
      });
    }

    onDeleteRuleTypeIndex(rule: GroupHeader): void {
      this.showConfirmDialog('Delete Confirmation', `Are you sure you want to delete this rule?`, (confirmed) => {
        if (confirmed) {
          this.viewModel.rules.filter(rl=>rl.ruleTypeIndex==rule.ruleTypeIndexValue).forEach(rul=>{
            const index = this.viewModel.rules.indexOf(rul);
            this.viewModel.rules.splice(index, 1);
          })
          this.viewModel.reindexRules();
  
          if ( this.viewModel.rules.length > 0 ) {
            const lastIndex = this.viewModel.rules.length - 1;
            //this.viewModel.selectedIngestConfig = this.viewModel.importDataViewModel.ingestConfigs[lastIndex];
          }
          else {
            //this.viewModel.selectedIngestConfig = null;
          }
          //this.dataSource.data = this.viewModel.rules;
          this.isRuleSetEmpty = this.viewModel.rules.length === 0;

          this.prepareRuleData();
          this.cdRef.detectChanges();
        }
      });
    }

    onDuplicateRule(sourceRule: ColumnCleanerRuleViewModel): void {
      
      const vm = new ColumnCleanerRuleViewModel(new ColumnCleanerRule(),this.matchValueEntryService);
      Object.assign(vm, sourceRule);
      
      this.viewModel.rules.splice(sourceRule.index, 0, vm);
      this.viewModel.reindexRules();
      //this.dataSource.data = this.viewModel.rules;
  
      //console.log(`rule inserted at index: ${sourceRule.index}`);

      this.prepareRuleData();
      if(vm.index==this.viewModel.rules.length){this.isScrollBottom=true; }else{this.isScrollBottom=false;}
      this.cdRef.detectChanges();
    }
    minrowcount:number=10;
    maxrowcount:number=100;
    isScrollBottom:boolean=true;
    addNewRule(): void {
      if (!this.viewModel.subTransformType && this.viewModel.subTransformType!=0) {
        this.displayError("Cannot add Rule unless select a subtransformer.","Please fix the validation error.");
        return;
      }
      const id = this.viewModel.rules.length;
      const rule = new ColumnCleanerRule();
      rule.ruleId = id;
      rule.index = id;

      rule.ruleTypeIndex = Math.max(...this.viewModel.rules.map(r => r.ruleTypeIndex), 0) + 1;
      rule.ruleType = this.viewModel.subTransformType;
      rule.fields = [];
      //rule.anonType = AnonType.Category;
      // rule.preservedTerms=[];
      // rule.mapTerms=undefined;
      // rule.mapOnly=false;
      rule.matchCase=false;
      rule.run=true;
      //set default value
      if(this.viewModel.subTransformType==RuleType.deleteRows){
        rule.ifMatched=true;
        rule.andOr=AndOr.orOr;
        rule.criteria=undefined;
        rule.matchCase=true;
      }else if(this.viewModel.subTransformType==RuleType.deDuplicate){
        rule.duplicatesType=DuplicateType.exclude;
        rule.duplicatesKeep=DuplicateKeep.first;
        rule.matchCase=true;
        if(this.inputDataFarmPreviewTable.displayedColumnInfos.find(op=>op.name=="row")){
          rule.fields.push("row");
        }else{
          rule.fields.push("None");
        }
      }else if(this.viewModel.subTransformType==RuleType.findReplace){
        rule.matchAll=false;
        rule.matchCase=true;
      }else if(this.viewModel.subTransformType==RuleType.findReplaceAdv){
        rule.matchAll=false;
        rule.criteria=Criteria.none;
        rule.andOr=AndOr.orOr;
        rule.matchCase=true;
      }else if(this.viewModel.subTransformType==RuleType.deleteColumns){
        rule.deleteWhenEmpty=1;
      }else if(this.viewModel.subTransformType==RuleType.combineColumns){
        rule.separator=SeparatorNone.none;
        rule.keepOrigColumn=true;
      }else if(this.viewModel.subTransformType==RuleType.splitColumns){
        rule.separator=SeparatorNone.pipe;
        rule.maxColumns=3;
        rule.keepOrigColumn=true;
      }else if(this.viewModel.subTransformType==RuleType.moveColumns){
        rule.overWriteData=false;
        rule.keepOrigColumn=false;
      }else if(this.viewModel.subTransformType==RuleType.formatColumns){
        rule.formatAs=FormatAs.title;
      }else if(this.viewModel.subTransformType==RuleType.splitintowords){
        rule.articleOption=Prepositions.nochange;
        rule.conjunctionOption=Prepositions.nochange;
        rule.determinerOption=Prepositions.nochange;
        rule.prepOption=Prepositions.nochange;
        rule.keepOrigColumn=true;
      }else if(this.viewModel.subTransformType==RuleType.removeChars){
        rule.removeChars="";
        rule.removeOption=RemoveOption.smartClean;
        rule.keepOrigColumn=true;
      }
      const vm = new ColumnCleanerRuleViewModel(rule,this.matchValueEntryService);
      if(this.viewModel.subTransformType==RuleType.deleteRows || this.viewModel.subTransformType==RuleType.findReplaceAdv){
        vm.matchValue=[];
        vm.matchValueList=[];
      }
      this.viewModel.rules.push(vm);
      this.isRuleSetEmpty = this.viewModel.rules.length === 0;
      this.isScrollBottom=true;
      //this.dataSource.data = this.viewModel.rules;
      //this.cacheSpan('ruleTypeIndex', d => d.ruleTypeIndex);
      
      this.prepareRuleData();
      this.viewModel.reindexRules();
      this.cdRef.detectChanges();
    }
    addSubRule(prule:ColumnCleanerRuleViewModel): void {
      const id = this.viewModel.rules.length;
      const rule = new ColumnCleanerRule();
      rule.ruleId = id;
      rule.index = id;
      rule.ruleTypeIndex = prule.ruleTypeIndex;
      rule.ruleType = prule.ruleType;
      rule.fields = [];
      rule.matchCase=false;
      rule.run=true;
    //set default value
    if(prule.ruleType==RuleType.deleteRows){
      rule.ifMatched=true;
      rule.andOr=AndOr.orOr;
      rule.matchCase=true;
      rule.criteria=undefined;
    }else if(prule.ruleType==RuleType.deDuplicate){
      rule.duplicatesType=DuplicateType.exclude;
      rule.duplicatesKeep=DuplicateKeep.first;
      rule.matchCase=true;
      if(this.inputDataFarmPreviewTable.displayedColumnInfos.find(op=>op.name=="row")){
        rule.fields.push("row");
      }else{
        rule.fields.push("None");
      }
    }else if(prule.ruleType==RuleType.findReplace){
      rule.matchAll=false;
      rule.matchCase=true;
    }else if(prule.ruleType==RuleType.findReplaceAdv){
      rule.matchAll=false;
      rule.criteria=Criteria.none;
      rule.andOr=AndOr.orOr;
      rule.matchCase=true;
    }else if(prule.ruleType==RuleType.deleteColumns){
      rule.deleteWhenEmpty=1;
    }else if(prule.ruleType==RuleType.combineColumns){
      rule.separator=SeparatorNone.none;
      rule.keepOrigColumn=true;
    }else if(prule.ruleType==RuleType.splitColumns){
      rule.separator=SeparatorNone.pipe;
      rule.maxColumns=3;
      rule.keepOrigColumn=true;
    }else if(prule.ruleType==RuleType.moveColumns){
      rule.overWriteData=false;
      rule.keepOrigColumn=false;
    }else if(prule.ruleType==RuleType.formatColumns){
      rule.formatAs=FormatAs.title;
    }else if(prule.ruleType==RuleType.splitintowords){
      rule.articleOption=Prepositions.nochange;
      rule.conjunctionOption=Prepositions.nochange;
      rule.determinerOption=Prepositions.nochange;
      rule.prepOption=Prepositions.nochange;
      rule.keepOrigColumn=true;
    }
    else if(this.viewModel.subTransformType==RuleType.removeChars){
      rule.removeChars="";
      rule.removeOption=RemoveOption.smartClean;
      rule.keepOrigColumn=true;
    }
      const vm = new ColumnCleanerRuleViewModel(rule,this.matchValueEntryService);
      if(prule.ruleType==RuleType.deleteRows || prule.ruleType==RuleType.findReplaceAdv){
        vm.matchValue=[];
        vm.matchValueList=[];
      }
      this.viewModel.rules.push(vm);
      this.isRuleSetEmpty = this.viewModel.rules.length === 0;
      //this.dataSource.data = this.viewModel.rules.sort((a,b)=>a.ruleTypeIndex-b.ruleTypeIndex);//test only
      //this.cacheSpan('ruleTypeIndex', d => d.ruleTypeIndex);
      
      this.prepareRuleData();
      this.viewModel.reindexRules();
      if(vm.index==this.viewModel.rules.length){this.isScrollBottom=true; }else{this.isScrollBottom=false;}
      this.cdRef.detectChanges();
    }
  
    getClassForRow(sender: PreviewTableComponent, row: any, colInfo: ColumnInfo, colInfos: ColumnInfo[]): string {
      try {
        return this.getClassForRowBase(sender, row, colInfo, colInfos);
      } catch (err) {
        return null;
      }
    }
  
    // ------------------------------------------------------------------
    // Copilot integration — base class handles lifecycle, we just implement
    // ------------------------------------------------------------------

    protected getCopilotTransformType(): number { return 7; }

    protected getCopilotColumnNames(): string[] {
      return this.viewModel.sourceColumnsDT?.map(c => c.text) ?? [];
    }

    protected getCopilotConfig(): any {
      return this.viewModel?.toTransformConfig() ?? {};
    }

    protected applyCopilotConfig(config: any, mode: 'replace' | 'merge' | 'preview'): void {
      if (!config?.rules) return;
      // 'preview' and 'replace' both set the full rule list — rules appear live as Copilot builds them.
      // 'merge' appends proposed rules after the existing ones.
      if (mode === 'merge') {
        const existing = this.viewModel.rules ?? [];
        const offset = existing.length;
        const merged = config.rules.map(
          (r: any, i: number) => new ColumnCleanerRuleViewModel(
            { ...r, index: offset + i }, this.matchValueEntryService
          )
        );
        this.viewModel.rules = [...existing, ...merged];
      } else {
        this.viewModel.rules = config.rules.map(
          (r: any) => new ColumnCleanerRuleViewModel(r, this.matchValueEntryService)
        );
      }
      this.prepareRuleData();
      this.cdRef.detectChanges();
    }

    // ------------------------------------------------------------------

    async saveConfig(): Promise<void> {
      if (!this.validate()) {
        return;
      }
      var valid= await this.validateBeforeRunSave();
      if (!valid) {
        return;
      }
  
      if (this.dataTransformConfigViewModel.dataTransformerConfig?.id || this.dataTransformConfigViewModel.dataTransformerConfig?.guid) {
        //console.log('updating...');
        this.saveConfigImpl(this.dataTransformConfigViewModel.dataTransformerConfig.name);
      } else {
        //console.log('saving...');
        this.showSaveConfigDialog(async (configName: string) => {
          this.saveConfigImpl(configName);
        });
      }
      
    }
  
    async saveConfigImpl(configName: string): Promise<void> {
        var currenttransformer_sampleStart = this.viewModel.parameters.sampleStart ;
      var currenttransformer_sampleEnd = this.viewModel.parameters.sampleEnd  ;
    
        if (currenttransformer_sampleStart >=  currenttransformer_sampleEnd) {
            this.displayWarning(
              'Sample Start was greater than to Sample End, so it was auto-reset to 1.',
              'Warning',
              undefined,
              () => {
                this.viewModel.parameters.sampleStart = 1;
              }
            );
            return;
          }
          if(currenttransformer_sampleStart > this.totalInputRows || currenttransformer_sampleEnd > this.totalInputRows )
        {
    
          this.displayWarning(
            'Sample Range exceeded the Input Farm size and was auto-corrected to the maximum limit.',
            'Warning',
            undefined,  // no exception to pass
            () => {
           
              if (currenttransformer_sampleStart > this.totalInputRows) {
              this.viewModel.parameters.sampleStart = 1;
              }
              if (currenttransformer_sampleEnd > this.totalInputRows) {
                this.viewModel.parameters.sampleEnd = this.totalInputRows;
              }
            }
          );
          return;
        }
      const busyIndicator = this.showBusyDialog('Please wait...');
      try {
        let config = this.dataTransformConfigViewModel.dataTransformerConfig ?? new DataTransformerConfig();
  
        config.name = configName;
        config.organizationId = 1;
        config.dataSourceType = this.dataTransformConfigViewModel.dataSourceType;
        config.selectedIngestFarmGuid = this.dataTransformConfigViewModel.selectedIngestFarmGuid;
        
        // Save the Data Connectors
        config.dataConnectorMappings = this.dataTransformConfigViewModel.ingestConfigs
        .map ( ingestConfig =>
          new DataConnectorMapping(
            ingestConfig.id,
            ingestConfig.name,
            ingestConfig.targetDataFarm,
            ingestConfig.dataConnector.toDataConnector(),
            ingestConfig.columnMap
              .map(i => i.toColumnMapDefinition()),
              ingestConfig.guid, 
              ingestConfig.masterColumnMapGuid)
        );
  
        //console.log('saving config...');
        //console.log(this.dataTransformConfigViewModel);
  
        // Save Transforms
        config.transforms = this.dataTransformConfigViewModel.transforms.map(t => t.transform);
  
        // Save the transform
        //const transform = config.transforms.find(t => t.id === this.viewModel.transform?.id) ?? this.viewModel.transformViewModel.transform;
        const transform = this.viewModel.transformViewModel.transform;
        //console.log(transform);
        
        //const extractorConfig: ColumnCleanerTransformConfig = transform.transformConfig ?? this.viewModel.toColumnCleanerTransformConfig();
        
        transform.transformConfig = this.viewModel.toTransformConfig();
        // transform.dataFarmName = this.viewModel.dataFarm.name;
        // transform.dataFarmType = this.viewModel.dataFarm.dataFarmType;
  
        // TODO: Maintain list of transforms for this config
        const existingTransform = config.transforms.find(t => t.guid === transform.guid);
        if (existingTransform) {
          config.transforms.splice(config.transforms.indexOf(existingTransform), 1);
        }      
  
        config.transforms.push(transform);
        // Save the ingest transforms
        //console.log(JSON.stringify(config));
  
        config = await this.configService.upsertConfig(config);
        //console.log(config);
        this.userData.setSelectedConfigJson(config);
        this.configViewModelService.setDataTransformConfigViewModel(new DataTransformConfigViewModel(config));
        this.userData.setSelectedTransform(this.dataTransformConfigViewModel.transforms.find(x=>x.guid=== this.userData.getSelectedTransform().guid));
        // SAve the current node status to Not RunConfigRequest.
        const selectedNode = this.userData.getSelectedNodeDetails();
        //const RefineryConfigJson = await this.configService.getRefineryConfigByGuidAsync(selectedNode.refineryConfigGuid);
        // //console.log('Refinery json in save = ', RefineryConfigJson) ;
        //RefineryConfigJson.nodes.filter(newNode => newNode.id == selectedNode.id)[0].data.nodeStatus = NodeRunStatus.NotRun
        //let RefineryJson = await this.configService.UpsertRefineryConfig(RefineryConfigJson);
  
        // var UpdateNodesStatus: UpdateNodesStatusRequest = new UpdateNodesStatusRequest(selectedNode.refineryConfigGuid, selectedNode.id, NodeRunStatus.PipelineModified )
        // let updateStatusreturn = await this.configService.updateUpcomingNodesStatus(UpdateNodesStatus);
  
        //Invoke the save current transformer cache api
       const activeTransformGuid = this.userData.getSelectedTransform().guid
       const userId = this.userData.getUserId()
       const orgId = this.userData.getOrgId()
       var saveCurrentTransformerCacheRequest: SaveCurrntTransfrmcacheRequest = new SaveCurrntTransfrmcacheRequest(userId, orgId, selectedNode.id, selectedNode.refineryConfigGuid, this.dataTransformConfigViewModel.selectedIngestFarmGuid, activeTransformGuid);
       await this.configService.saveCurrentTransformerCache(saveCurrentTransformerCacheRequest);
       await this.configService.getRefineryConfigByGuidAsync(this.userData.getRefineryId());
       this.getCacheStatus();
       //update preViewModel
        this.snapshotPreState();

       // this.cdRef.detectChanges();
        this.snackBar.open(`Successfully saved transform pipeline '${this.userData.getSelectedTransform().name}'`, 'Dismiss', {
          duration: 5000
        });
        busyIndicator.close();
  
      } catch (err) {
        busyIndicator.close();
        this.displayError(`Failed to save '${this.userData.getSelectedTransform().name}'`, 'Error', err);
      }
    }
  
    onSetDefaultRule(defaultfield:string){
      // if(defaultfield=="sourceColumn"){
      //   this.viewModel.rules.forEach(rule=>{
      //     rule.sourceColumn=this.viewModel.parameters.sourceColumn;
      //   })
      // }
      // else if(defaultfield=="targetColumn"){
      //   this.viewModel.rules.forEach(rule=>{
      //     rule.targetColumn=this.viewModel.parameters.targetColumn;
      //   })
      // }
    }
    totalRulesColumnDisplay(){
      //blank values
      this.viewModel.rules.forEach(rule=>{
        rule.rowHits=null;
      })
      if(this.viewModel.parameters.runAll){
        this.displayedColumns= [
          'actions',
          'index',
          'name',
          'fields',
          'anonType',
          'preservedTerms',
          'mapTerms',
          'mapOnly',
          'matchCase',
          'isEnabled',
          'sampleHits',
          'hits'];
        }else{
        this.displayedColumns= [
          'actions',
          'index',
          'name',
          'fields',
          'anonType',
          'preservedTerms',
          'mapTerms',
          'mapOnly',
          'matchCase',
          'isEnabled',
          'sampleHits',
          'hits'];
      }
      this.cdRef.detectChanges();
    }
    onToggleRulesColumnDisplay(){
      //this.totalRulesColumnDisplay();
    }
    onToggleMultiTarget(event: any){
      //this.viewModel.multiTargetColumn=event.checked;
    }
    onToggleSpaceSeparator(event: any){
      //this.viewModel.spaceSeparator=event.checked;
    }
    onToggleRemoveRedundant(event: any){
      //this.viewModel.removeRedundant=event.checked;
    }
    onParameterFormStatusChange(status: boolean) {
      this.isParameterFormValid = status;
    }

    checkContainsListLength(rule: any): boolean {
      return !rule.containsList || rule.containsList.length <= 1;
    }   
  
    onTabClick(event: MatTabChangeEvent): void {
      const selectedTabIndex = event.index;
      const selectedTabLabel = event.tab.textLabel;
      this.selectedIndex = selectedTabIndex;
   
      // Alert the tab index and label
     // alert(`Tab clicked: ${selectedTabLabel} (Index: ${selectedTabIndex})`);
     this.userData.setResultsTabClicked(false);
     if(selectedTabLabel.match("Output") && selectedTabIndex == 1) {
      this.userData.setOutputTabClicked(true)
      this.setOutputPreviewTabSlctnStatus();
     } else {
        this.userData.setOutputTabClicked(false);
        this.setInputPreviewFilterViewModel();
        // if(selectedTabLabel.match("Summary") && selectedTabIndex == 2) {
        //   if(this.source.length > 0) {
        //     let selectFilterInfoSummary = new SelectFilterInfo(this.source, this.selectFilterValues);
        //     this.onApplySelect(selectFilterInfoSummary);
        //   }
        // }
     }
    }
  
    setColumnCleanerResultsTabClicked(): void {
      if(this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey.length > 0) {
        this.transformerInputPreviewCacheKey = this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey;
      }
      
      this.userData.setOutputTabClicked(false);
      this.userData.setResultsTabClicked(true);
      this.resetTransformerInputPrvwModelData();
      this.userData.setInputTabDataFrFilter(this.inputPrvwFilterViewModel);
      this.isResultsPanelExpanded = true;
      this.isPreviewPanelExpanded = false;
    }
  
    setColumnCleanerPreviewTabClicked(): void {
      if(this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey.length > 0) {
        this.transformerInputPreviewCacheKey = this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey;
      }
      this.userData.setResultsTabClicked(false);
      this.userData.setOutputTabClicked(false);
      this.isResultsPanelExpanded = false;
      this.isPreviewPanelExpanded = true;
      if(this.selectedIndex == 0) {
        this.setInputPreviewFilterViewModel();
      }
      if(this.selectedIndex == 1) {
        this.setOutputPreviewTabSlctnStatus();
      }
    }
  
    setOutputPreviewTabSlctnStatus(): void {
      this.userData.setResultsTabClicked(false);
      this.userData.setOutputTabClicked(true);
      this.resetTransformerInputPrvwModelData();
      this.userData.setInputTabDataFrFilter(this.inputPrvwFilterViewModel);
    }
  
    setInputPreviewFilterViewModel() {
      this.inputPrvwFilterViewModel.previewType = "TransformerInput";
      this.setTransformerInputPrvwCacheKey();
      this.userData.setInputTabDataFrFilter(this.inputPrvwFilterViewModel)
    }
    
    setTransformerInputPrvwCacheKey() {
      if(this.transformerInputPreviewCacheKey.length > 0 && this.inputPrvwFilterViewModel.farmId.length > 0) {
        this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey = this.transformerInputPreviewCacheKey;
        this.userData.setInputTabDataFrFilter(this.inputPrvwFilterViewModel);
      }
    }
    resetInputPrvwViewModel() {
      this.inputPrvwFilterViewModel.farmId = "";
      this.inputPrvwFilterViewModel.collookupFarmId = "";
      this.inputPrvwFilterViewModel.nodeType = "";
      this.inputPrvwFilterViewModel.previewType = "TransformerInput";
      this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey = "";
    }
  
    resetTransformerInputPrvwModelData() {
      this.inputPrvwFilterViewModel.transfrmrInputprvwCacheKey = "";
      this.inputPrvwFilterViewModel.previewType = "";
    }
  
    get isValidateParameters(): boolean {
      return false;//!this.isParameterFormValid;
    }
    get isValidateAdvanceSettings(): boolean {
      return this.form.invalid;
    }
    
    onRuleFieldSelectCellClicked(rule: ColumnCleanerRuleViewModel): void {
      let title: string='Source Field Chooser: Rule '+rule.index;
      let label: string="Field Name";
      let placeholder: string="Enter Value";
      let fieldType: string ="autocomplete";
      let isDuplicateRemoved: boolean =true;
      let canAddNewAutocomplete: boolean =false;
      var options: string[] = [];
      let dialogWidth: string='500px'

      if(rule.ruleType==RuleType.deleteRows){
        if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.bool){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)=="boolean")).forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.number){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)=="number")).forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.timestamp){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>this.getNormalizedDataType(col.pydataType)=="datetime").forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.string){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>this.getNormalizedDataType(col.pydataType)=="string").forEach(map=>{
            options.push(map.name);
          });
        }else {
          this.inputDataFarmPreviewTable.displayedColumnInfos.forEach(map=>{
            options.push(map.name);
          });
        }
      }else if(rule.ruleType==RuleType.formatColumns){
        this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)!="boolean")).forEach(map=>{
          options.push(map.name);
        });
      }else if(rule.ruleType==RuleType.deleteColumns){
        this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>col.name!='row').forEach(map=>{
            options.push(map.name);
          });
      }else{
        this.inputDataFarmPreviewTable.displayedColumnInfos.forEach(map=>{
            options.push(map.name);
          });
      }
      // Get unique values
      var options = Array.from(new Set(options)).sort();

      if(options.length==0){
        this.displayInfo("There are no source fields available for this criteria. Please choose a different criteria.","No Fields for This Criteria.");
        return;
      }
      
      this.showTextEditorDialogOption(title, label,placeholder,fieldType,isDuplicateRemoved,canAddNewAutocomplete,options,dialogWidth, 
        rule.fields,
        async (items: ColoredTextItem[],eventname:string) => {
          var validate=true;
          if(rule.ruleType==RuleType.deleteRows){
            if (items.length == 0) {
              validate=false;
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "No Source Fields are selected, please add one or more."),"Please fix the validation error."
              ,"", onClosed=> {
                //this.onRuleFieldSelectCellClicked(rule);
              });
            }
          }
          if(eventname=='save'){
            if(validate){
              rule.fields = items;
            }
          }
      });
    }
    
    onRuleFieldSelectCellClickedConditional(rule: ColumnCleanerRuleViewModel): void {
      let title: string='Source Field Chooser: Rule '+rule.index;
      let label: string="Field Name";
      let placeholder: string="Enter Value";
      let fieldType: string ="autocomplete";
      let isDuplicateRemoved: boolean =true;
      let canAddNewAutocomplete: boolean =false;
      var options: string[] = [];
      let dialogWidth: string='500px'

      if(rule.ruleType==RuleType.findReplaceAdv){
        if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.bool){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)=="boolean")).forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.number){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)=="number")).forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.timestamp){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>this.getNormalizedDataType(col.pydataType)=="datetime").forEach(map=>{
            options.push(map.name);
          });
        }else if(this.getDataTypeCriteria(rule.criteria)==DataTypeCriteria.string){
          this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>this.getNormalizedDataType(col.pydataType)=="string").forEach(map=>{
            options.push(map.name);
          });
        }else {
          this.inputDataFarmPreviewTable.displayedColumnInfos.forEach(map=>{
            options.push(map.name);
          });
        }
      }else if(rule.ruleType==RuleType.formatColumns){
        this.inputDataFarmPreviewTable.displayedColumnInfos.filter(col=>(this.getNormalizedDataType(col.pydataType)!="boolean")).forEach(map=>{
          options.push(map.name);
        });
      }else{
        this.inputDataFarmPreviewTable.displayedColumnInfos.forEach(map=>{
            options.push(map.name);
          });
      }
      
      
      // Get unique values
      var options = Array.from(new Set(options)).sort();

      if(options.length==0){
        this.displayInfo("There are no source fields available for this criteria. Please choose a different criteria.","No Fields for This Criteria.");
        return;
      }
      this.showTextEditorDialogOption(title, label,placeholder,fieldType,isDuplicateRemoved,canAddNewAutocomplete,options,dialogWidth, 
        rule.conditionalFields,
        async (items: ColoredTextItem[],eventname:string) => {
          var validate=true;
          if(rule.ruleType==RuleType.deleteRows){
            if (items.length == 0) {
              validate=false;
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "No Source Fields are selected, please add one or more."),"Please fix the validation error."
              ,"", onClosed=> {
                //this.onRuleFieldSelectCellClickedConditional(rule);
              });
            }
          }
          if(eventname=='save'){
            if(validate){
              rule.conditionalFields = items;
            }
          }
      });
    }
    onRuleFieldSelectCellClickedDeDuplicate(rule: ColumnCleanerRuleViewModel): void {
      let title: string='Source Field Chooser: Rule '+rule.index;
      let label: string="Field Name";
      let placeholder: string="Enter Value";
      let fieldType: string ="autocomplete";
      let isDuplicateRemoved: boolean =true;
      let canAddNewAutocomplete: boolean =false;
      var options: string[] = [];
      let dialogWidth: string='500px'

      if(rule.duplicatesType==DuplicateType.exclude){
        options.push("None");
      }else{
        options.push("All");
      }
      this.inputDataFarmPreviewTable.displayedColumnInfos.forEach(map=>{
          options.push(map.name);
        });
      
      // Get unique values
      var options = Array.from(new Set(options)).sort();
      this.showTextEditorDialogOption(title, label,placeholder,fieldType,isDuplicateRemoved,canAddNewAutocomplete,options,dialogWidth, 
        rule.fields,
        async (items: ColoredTextItem[],eventname:string) => {
          var validate=true;
          if(rule.ruleType==RuleType.deleteRows){
            if (items.length == 0) {
              validate=false;
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "No Source Fields are selected, please add one or more."),"Please fix the validation error."
              ,"", onClosed=> {
                this.onRuleFieldSelectCellClicked(rule);
              });
            }
          }
          if(eventname=='save'){
            if(validate){
              if(rule.ruleType==RuleType.deDuplicate){
                if(items.length===0){
                  rule.fields=[];
                  if(rule.duplicatesType===DuplicateType.exclude){
                    rule.fields.push(new ColoredTextItem("None",NamedColorList[0]));
                  }else if(rule.duplicatesType===DuplicateType.include){
                    rule.fields.push(new ColoredTextItem("All",NamedColorList[0]));
                  }
                }else{
                    rule.fields = items;
                }
              }else{
                rule.fields = items;
              }
            }
          }
      });
    }
    
    onFindReplaceCellClicked(rule: ColumnCleanerRuleViewModel): void {
      let title: string=`Chooser (Find & Replace): Rule ${rule.index}${rule.ruleName ? ' - ' + rule.ruleName : ''}`;
      let labelFind: string="Find";
      let labelReplace: string="Replace";
      let placeholderFind: string="Enter find";
      let placeholderReplace: string="Enter replace";
      let fieldType: string="edit";
      let isDuplicateRemoved: boolean=false;
      let canAddNewAutocomplete: boolean=false;
      let optionsFind: OptionItemDataType[]=[];
      let optionsReplace: OptionItemDataType[]=[];
      let validateboth: boolean=false;
      let isDataTypeCheck: boolean=false;
      let dialogWidth: string='650px';
      this.showFindReplaceEditorDialogOption(title,labelFind,labelReplace,placeholderFind,placeholderReplace,fieldType,isDuplicateRemoved,canAddNewAutocomplete,optionsFind,optionsReplace,validateboth,isDataTypeCheck,dialogWidth,
        [...rule.findReplace],
        async (items: ColoredTextFindReplaceItem[],eventname:string) => {
          if(eventname=='save'){
          rule.findReplace = items;
          }
        });
    }

    cacheSpan(key: string, accessor: (d: ColumnCleanerRuleViewModel) => any) {
      for (let i = 0; i < this.dataSourceRule.length;) {
        const currentRow = this.dataSourceRule[i];
    
        // Skip GroupHeader rows
        if (!this.isDataItem(currentRow)) {
          i++; // Move to the next item
          continue;
        }
    
        let currentValue = accessor(currentRow);
        let count = 1;
    
        for (let j = i + 1; j < this.dataSourceRule.length; j++) {
          const nextRow = this.dataSourceRule[j];
    
          // Skip GroupHeader rows and stop counting when values differ
          if (!this.isDataItem(nextRow) || currentValue !== accessor(nextRow)) {
            break;
          }
          count++;
        }
    
        if (!this.spans[i]) {
          this.spans[i] = {};
        }
    
        this.spans[i][key] = count;
        i += count;
      }
    }
    /** Type Guard to check if the object is a DataItem */
    isDataItem(row: ColumnCleanerRuleViewModel | GroupHeader): row is ColumnCleanerRuleViewModel {
      return (row as ColumnCleanerRuleViewModel).name !== undefined;
    }
    getRowSpan(col: string, index: number): number | undefined {
      return this.spans[index] && this.spans[index][col];
    }
    onRuleMatchValueSelectCellClicked(rule: ColumnCleanerRuleViewModel): void {
      let title: string='Match Value  (Rule '+rule.index+' - String '+CriteriaUtil.toString(rule.criteria)+')';
      let label: string="Match Value";
      let placeholder: string="Enter Value";
      let fieldType: string ="edit";
      let isDuplicateRemoved: boolean =false;
      let canAddNewAutocomplete: boolean =false;
      let options: string[] = [];
      let dialogWidth: string='500px'
      this.showTextEditorDialogOption(title, label,placeholder,fieldType,isDuplicateRemoved,canAddNewAutocomplete,options,dialogWidth, 
        rule.matchValue,
        async (items: ColoredTextItem[],eventname:string) => {
          if(eventname=='save'){
            rule.matchValue = items;
          }
      });
    }
    MatchValueEntryType=MatchValueEntryType;
    MatchValueEntryEditor(criteria: Criteria,andOr:AndOr): MatchValueEntryType {
      return this.matchValueEntryService.MatchValueEntryEditor(criteria,andOr);
    }
    isDisabledAndOr(criteria: Criteria): boolean {
      return [
        //Criteria.empty,
        //Criteria.boolTrue,
        //Criteria.boolFalse,
        Criteria.equals,
        Criteria.lessThan,
        Criteria.greaterThan,
        Criteria.LessThanOrEqualTo,
        Criteria.greaterThanOrEqualTo,
        Criteria.lastnDays,
        //Criteria.equalToday,
        Criteria.between,
        Criteria.inclusiveLeft,
        Criteria.inclusiveRight,
        Criteria.inclusiveBoth,
        Criteria.columnMatch
      ].includes(criteria);
    }
    isDisabledMatch(criteria: Criteria): boolean {
      return [
        //Criteria.empty,
        //Criteria.boolTrue,
        //Criteria.boolFalse,
        Criteria.equals,
        Criteria.lessThan,
        Criteria.greaterThan,
        Criteria.LessThanOrEqualTo,
        Criteria.greaterThanOrEqualTo,
        Criteria.lastnDays,
        //Criteria.equalToday,
        Criteria.between,
        Criteria.inclusiveLeft,
        Criteria.inclusiveRight,
        Criteria.inclusiveBoth,
        Criteria.length,
        Criteria.none,
        Criteria.columnMatch
      ].includes(criteria);
    }
    isAndOrTwoValues(criteria: Criteria): boolean {
      return [
        Criteria.empty,
        Criteria.boolTrue,
        Criteria.boolFalse,
        Criteria.equalToday,
      ].includes(criteria);
    }
    getGroupAndOrCriteria(criteria: Criteria): string {
      if ([Criteria.matches,
        Criteria.contains,
        Criteria.startsWith,
        Criteria.endsWith,
        Criteria.between].includes(criteria)) {
        return "A";
      } else if ([Criteria.empty,
        Criteria.boolTrue,
        Criteria.boolFalse,
        Criteria.equals,
        Criteria.lessThan,
        Criteria.between,
        Criteria.equalToday,
        Criteria.lastnDays,
        Criteria.length].includes(criteria)) {
        return "B";
      }else{
        return "";
      }
    }
    onRuleMatchValueSelectCellClickedTextEntry(rule: ColumnCleanerRuleViewModel): void {
      var title='Text Entry ('+CriteriaUtil.toString(rule.criteria)+')';
      var isShowHeader=true;

      var sourcefields=[];
      if(rule.ruleType==RuleType.findReplaceAdv){
        sourcefields=rule.conditionalFields;
      }else{
        sourcefields=rule.fields;
      }

      if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiSingle){
          if(rule.matchValue.length==0){
            let colorIndex = rule.matchValue.length;
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }else{
            while (rule.matchValue.length >1) {
              rule.matchValue.pop();
            }
          }
        }else{
          //same the length
          if(sourcefields.length !== rule.matchValue.length){
            let colorIndex = rule.matchValue.length;
            // If rule.matchValue is shorter, add blank items
            while (rule.matchValue.length < sourcefields.length) {
              rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
            }

            // If rule.matchValue is longer, remove excess items
            while (rule.matchValue.length > sourcefields.length) {
              rule.matchValue.pop();
            }
          }
        }

        this.showColoredTextItemEditorDialogTextEntry(title, rule.matchValue, sourcefields,isShowHeader,async (items: ColoredTextItem[]) => {
          rule.matchValue = items;
        });
    }
    onRuleMatchValueSelectCellClickedValueEntry(rule: ColumnCleanerRuleViewModel): void {
      var title='Value Entry ('+CriteriaUtil.toString(rule.criteria)+')';
      var symbol: string =CriteriaUtilSymbol.toString(rule.criteria);
      var isShowHeader=true;
      var isAddNew=true;
      var label="Value";
      var placeholder="Enter numeric value";
      var isSpinner=false;
      var isPositiveInteger=false;

      var sourcefields=[];
      if(rule.ruleType==RuleType.findReplaceAdv){
        sourcefields=rule.conditionalFields;
      }else{
        sourcefields=rule.fields;
      }

      if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueSingle){
        isShowHeader=false;
        isAddNew=true;
        if(rule.criteria==Criteria.lastnDays){
          isAddNew=false;
          if(rule.matchValue.length==0){
            let colorIndex = rule.matchValue.length;
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }else{
            while (rule.matchValue.length >1) {
              rule.matchValue.pop();
            }
          }
        }
      }else{
        isShowHeader=true;
        isAddNew=false;
        //same the length
        if(sourcefields.length !== rule.matchValue.length){
          let colorIndex = rule.matchValue.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValue.length < sourcefields.length) {
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValue.length > sourcefields.length) {
            rule.matchValue.pop();
          }
        }
        }
      
      if(rule.criteria==Criteria.lastnDays){
        isSpinner=true;
        isPositiveInteger=true;
      }  
      
      this.showColoredTextItemEditorDialogValueEntry(title,symbol, rule.matchValue, sourcefields,rule.andOr,isShowHeader,isAddNew,label,placeholder,isSpinner,isPositiveInteger,async (items: ColoredTextItem[]) => {
          rule.matchValue = items;
        });
    }
    onRuleMatchValueSelectCellClickedRangeEntry(rule: ColumnCleanerRuleViewModel): void {
      var title='Range Entry ('+CriteriaUtil.toString(rule.criteria)+')';
      var symbol: string =CriteriaUtilSymbol.toString(rule.criteria);
      var isShowHeader=true;
      var isAddNew=true;
      var findLabel="Start";
      var replaceLabel="End";
      var findPlaceholder="Enter Start Range";
      var replacePlaceholder="Enter End Range";
      var isSpinner=false;
      var isPositiveInteger=false;

      var sourcefields=[];
      if(rule.ruleType==RuleType.findReplaceAdv){
        sourcefields=rule.conditionalFields;
      }else{
        sourcefields=rule.fields;
      }

      if(rule.criteria==Criteria.length){
        findPlaceholder="Start Length";
        replacePlaceholder="End Length";
        isSpinner=true;
        isPositiveInteger=true;
      }
      if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeSingle){
          isShowHeader=false;
          isAddNew=true;
          
          // if(rule.matchValueList.length==0){
          //   let colorIndex = rule.matchValueList.length;
          //   rule.matchValueList.push(new ColoredTextFindReplaceItem("","", NamedColorList[colorIndex++]));
          // }else{
          //   while (rule.matchValueList.length >1) {
          //     rule.matchValueList.pop();
          //   }
          // }
        }else{
          isShowHeader=true;
          isAddNew=false;
          //same the length
          if(sourcefields.length !== rule.matchValueList.length){
            let colorIndex = rule.matchValueList.length;
            // If rule.matchValue is shorter, add blank items
            while (rule.matchValueList.length < sourcefields.length) {
              rule.matchValueList.push(new ColoredTextFindReplaceItem("0","1", NamedColorList[colorIndex++]));
            }

            // If rule.matchValue is longer, remove excess items
            while (rule.matchValueList.length > sourcefields.length) {
              rule.matchValueList.pop();
            }
          }
        }
      
      this.showColoredTextItemEditorDialogRangeEntry(title,symbol, rule.matchValueList, sourcefields,rule.andOr,isShowHeader,isAddNew,findLabel,replaceLabel,findPlaceholder,replacePlaceholder,isSpinner,isPositiveInteger,async (items: ColoredNumberFindReplaceItem[]) => {
        rule.matchValueList = items.map(item => this.convertToColoredTextFindReplaceItem(item));
        });
    }
    convertToColoredTextFindReplaceItem(item: ColoredNumberFindReplaceItem): ColoredTextFindReplaceItem {
      return {
          find: item.find.toString(), 
          replace: item.replace.toString(), 
          color: item.color
      };
    }
  
    showColoredTextItemEditorDialogTextEntry(
      title: string,
      existingItems: ColoredTextItem[],
      sourceItems: ColoredTextItem[],
      isShowHeader:boolean,
      onClosed: (items: ColoredTextItem[]) => any ): void {

      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.data = {items: existingItems, title,isShowHeader,sourceItems:sourceItems};
      dialogConfig.width = '400px';
    
      const dialogRef = this.matDialog.open(TextEntryFieldComponent, dialogConfig);

      dialogRef.afterClosed().subscribe(result => {
        if(result.eventname=='save'){
              onClosed(result.items);
        }
      });
    }
    showColoredTextItemEditorDialogValueEntry(
      title: string,
      symbol: string,
      existingItems: ColoredTextItem[],
      sourceItems: ColoredTextItem[],
      andOr:AndOr,
      isShowHeader:boolean,
      isAddNew:boolean,
      label: string,
      placeholder: string,
      isSpinner:boolean,
      isPositiveInteger:boolean,
      
      onClosed: (items: ColoredTextItem[]) => any ): void {

      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.data = {items: existingItems, title,symbol,isShowHeader,sourceItems:sourceItems,isAddNew,label,placeholder,isSpinner,isPositiveInteger};
      if(this.matchValueEntryService.GetGroupAndOr(andOr)==AndOrGroup.nonPaired){
        dialogConfig.width = '350px';
      }else{
        dialogConfig.width = '400px';
      }
    
      const dialogRef = this.matDialog.open(ValueEntryFieldComponent, dialogConfig);

      dialogRef.afterClosed().subscribe(result => {
        if(result.eventname=='save'){
              onClosed(result.items);
        }
      });
    }
    showColoredTextItemEditorDialogRangeEntry(
      title: string,
      symbol: string,
      existingItems: ColoredTextFindReplaceItem[],
      sourceItems: ColoredTextItem[],
      andOr:AndOr,
      isShowHeader:boolean,
      isAddNew:boolean,
      findLabel: string,
      replaceLabel: string,
      findPlaceholder: string,
      replacePlaceholder: string,
      isSpinner:boolean,
      isPositiveInteger:boolean,
      onClosed: (items: ColoredNumberFindReplaceItem[]) => any ): void {

      const dialogConfig = new MatDialogConfig();
      dialogConfig.disableClose = true;
      dialogConfig.autoFocus = true;
      dialogConfig.data = {items: existingItems, title,symbol,isShowHeader,sourceItems:sourceItems,isAddNew,findLabel,replaceLabel,findPlaceholder,replacePlaceholder,isSpinner,isPositiveInteger};

      if(this.matchValueEntryService.GetGroupAndOr(andOr)==AndOrGroup.nonPaired){
       // dialogConfig.width = '480px';
         dialogConfig.width = '450px';
      }else{
      //  dialogConfig.width = '510px';
       dialogConfig.width = '480px';
      }
    
      const dialogRef = this.matDialog.open(RangeEntryFieldComponent, dialogConfig);

      dialogRef.afterClosed().subscribe(result => {
        if(result.eventname=='save'){
              onClosed(result.items);
        }
      });
    }

    // Store previous value before selection changes
    private previousValues: number;
    storePreviousValue(value: number) {
      this.previousValues=value; 
    }

    onCriteriaChange(rule: ColumnCleanerRuleViewModel) {
      if(rule.fields.length>0){
        var preCriteria=Number(this.previousValues) as Criteria;
        if(this.getGroupCriteria(preCriteria)!=this.getGroupCriteria(rule.criteria)){
          // if(this.isDisabledAndOr(rule.criteria)){
          //   rule.andOr=undefined;
          // }else{
          //   rule.andOr=AndOr.orOr;
          // }
          if(this.isDisabledMatch(rule.criteria)){
            rule.matchCase=undefined;
          }else{
            rule.matchCase=true;
          }
          if(this.isAndOrTwoValues(preCriteria)!=this.isAndOrTwoValues(rule.criteria)){
            if(rule.andOr==AndOr.orOr || rule.andOr==AndOr.orAnd){
              rule.andOr=AndOr.orOr;
            }else if(rule.andOr==AndOr.andOr || rule.andOr==AndOr.andAnd){
              rule.andOr=AndOr.andOr;
            }else{
              rule.andOr=AndOr.orOr;
            }
          }
          
          this.showConfirmDialog(
              'Change Confirmation', 
              'Change to the Criteria Category will clear the Source Field(s) selections.', 
              (confirmed) => {
                  if (confirmed) {
                      rule.fields = [];
                      rule.matchValue=[];
                      rule.matchValueList=[];
                      this.onRuleFieldSelectCellClicked(rule);// need more cond if criteria is undefined
                  } else {
                      rule.criteria = preCriteria; // Revert if canceled
                  }
              }
          );
        }else{
          if(this.getGroupCriteria(rule.criteria)=='D'){
            if(this.matchValueEntryService.MatchValueEntryEditor(preCriteria,rule.andOr)!=this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)){
              rule.matchValue=[];
              rule.matchValueList=[];
            }
          }
        }
      }
      else{
        if(rule.criteria || rule.criteria==0){
          // if(this.isDisabledAndOr(rule.criteria)){
          //   rule.andOr=undefined;
          // }else{
          //   rule.andOr=AndOr.orOr;
          // }
          if(this.isDisabledMatch(rule.criteria)){
            rule.matchCase=undefined;
          }else{
            rule.matchCase=true;
          }

          // this.showOkDialogWarning(
          //   'Information', 
          //   'Source Field(s) must be selected after Criteria is selected.',
          //   (onClosed) => this.onRuleFieldSelectCellClicked(rule)
          // );
        }
      }
    }
    onCriteriaChangeAdv(rule: ColumnCleanerRuleViewModel) {
      if(rule.criteria==Criteria.none){
        rule.conditionalFields = [];
        rule.matchValue=[];
        rule.andOr=AndOr.orOr;
        rule.matchCase=false;
        return;
      }
      if(rule.conditionalFields.length>0){
        var preCriteria=Number(this.previousValues) as Criteria;
        if(this.getGroupCriteria(preCriteria)!=this.getGroupCriteria(rule.criteria)){
          // if(this.isDisabledAndOr(rule.criteria)){
          //   rule.andOr=undefined;
          // }else{
          //   rule.andOr=AndOr.orOr;
          // }
          if(this.isDisabledMatch(rule.criteria)){
            rule.matchCase=undefined;
          }else{
            rule.matchCase=true;
          }
          if(this.isAndOrTwoValues(preCriteria)!=this.isAndOrTwoValues(rule.criteria)){
            if(rule.andOr==AndOr.orOr || rule.andOr==AndOr.orAnd){
              rule.andOr=AndOr.orOr;
            }else if(rule.andOr==AndOr.andOr || rule.andOr==AndOr.andAnd){
              rule.andOr=AndOr.andOr;
            }else{
              rule.andOr=AndOr.orOr;
            }
          }
          
          this.showConfirmDialog(
              'Change Confirmation', 
              'Change to the Criteria Category will clear the Field(s) selections.', 
              (confirmed) => {
                  if (confirmed) {
                      rule.conditionalFields = [];
                      rule.matchValue=[];
                      rule.matchValueList=[];
                      this.onRuleFieldSelectCellClickedConditional(rule);// need more cond if criteria is undefined
                  } else {
                      rule.criteria = preCriteria; // Revert if canceled
                  }
              }
          );
        }else{
          if(this.getGroupCriteria(rule.criteria)=='D'){
            if(this.matchValueEntryService.MatchValueEntryEditor(preCriteria,rule.andOr)!=this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)){
              rule.matchValue=[];
              rule.matchValueList=[];
            }
          }
        }
      }
      else{
        //rule.preCriteria=rule.criteria; // for Revert save 1st time value
        if(rule.criteria || rule.criteria==0){
          // if(this.isDisabledAndOr(rule.criteria)){
          //   rule.andOr=undefined;
          // }else{
          //   rule.andOr=AndOr.orOr;
          // }
          if(this.isDisabledMatch(rule.criteria)){
            rule.matchCase=undefined;
          }else{
            rule.matchCase=true;
          }
          if(rule.matchValue==undefined){
            rule.matchValue=[];
          }
          if(rule.matchValueList==undefined){
            rule.matchValueList=[];
          }
          // this.showOkDialogWarning(
          //   'Information', 
          //   'Field(s) must be selected after Criteria is selected.',
          //   (onClosed) => this.onRuleFieldSelectCellClickedConditional(rule)
          // );
        }
      }     
    }
    getGroupCriteria(criteria: Criteria): string {
      if([
        Criteria.empty,
        Criteria.none,
        Criteria.columnMatch].includes(criteria)){
        return "A";
      }else if([
        Criteria.matches,
        Criteria.contains,
        Criteria.notContains,
        Criteria.startsWith,
        Criteria.endsWith,
        Criteria.length].includes(criteria)){
        return "B";
      }else if([
        Criteria.boolTrue,
        Criteria.boolFalse].includes(criteria)){
        return "C";
      }else if([
        Criteria.equals,
        Criteria.lessThan,
        Criteria.greaterThan,
        Criteria.LessThanOrEqualTo,
        Criteria.greaterThanOrEqualTo,
        Criteria.between,
        Criteria.inclusiveLeft,
        Criteria.inclusiveRight,
        Criteria.inclusiveBoth].includes(criteria)){
        return "D";
      }else if([
        Criteria.lastnDays,
        Criteria.equalToday].includes(criteria)){
        return "E";
      }else{
        return "None";
      }
    }

    async validateBeforeRunSave():Promise<boolean> {
      // Warnings never block: shown after every error check has passed (showRuleWarnings).
      const warnings: string[] = [];
      //Validate Rules Table fields and target column -- only rules that are on (a rule that
      //is off is not run, so it is not checked: TransformRuleViewModel.enabledRules)
      for (const rule of TransformRuleViewModel.enabledRules(this.viewModel.rules)) {
        if (rule.ruleType== RuleType.splitColumns){
          if (rule.splitField== "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be one selected field for the Source Field."),"Please fix the validation error.");
            return false;
          }
        }else if(rule.ruleType== RuleType.findReplaceAdv && rule.criteria!=Criteria.none){
          if (rule.conditionalFields.length == 0) {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be one selected field for the Supplemental Condition field."),"Please fix the validation error.");
            return false;
          }
        }else if(rule.ruleType== RuleType.moveColumns){
          if (rule.moveColumns.length == 0) {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be one selected field for the Move Columns field."),"Please fix the validation error.");
            return false;
          }
        }else if (rule.ruleType== RuleType.splitintowords){
          if (rule.splitField== "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be one selected field for the Source Field."),"Please fix the validation error.");
            return false;
          }else if (!rule.targetField || rule.targetField == "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be selected one field for the Target Field."),"Please fix the validation error.");
            return false;
          }
        }else if (rule.ruleType== RuleType.removeChars){
          if (rule.removeChars== "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be value in the Remove Char Field."),"Please fix the validation error.");
            return false;
          }else if (!rule.targetField || rule.targetField == "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be selected one field for the Target Field."),"Please fix the validation error.");
            return false;
          }
        }else{
          if (rule.fields.length == 0) {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be one selected field for the Source Field."),"Please fix the validation error.");
            return false;
          }
        }
        // if (rule.ruleType== RuleType.splitColumns){
        //   if (!rule.targetField || rule.targetField == "") {
        //     this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be selected one field for the Source Field."),"Please fix the validation error.");
        //     return false;
        //   }
        // }
        if (rule.ruleType== RuleType.combineColumns) {
          // Combine with one Source Field: a warning from the shared pre-run check (one engine).
          if (!rule.targetField || rule.targetField == "") {
            this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "There must be selected one field for the Target Field."),"Please fix the validation error.");
            return false;
          }
        }
        if (rule.ruleType== RuleType.deleteRows || rule.ruleType== RuleType.findReplaceAdv) {
          var fieldname="";
          var sourcefields=[];
          if (rule.ruleType== RuleType.deleteRows) {
            fieldname="Source field";
            sourcefields=rule.fields;
          }else{
            fieldname="Supplemental Condition field";
            sourcefields=rule.conditionalFields;
          }
          if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeSingle){
            if(rule.matchValueList && rule.matchValueList.length!=0){
              for (const matchv of rule.matchValueList) {
                if(matchv.find=="" || matchv.replace==""){
                  this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
                  return false;
                }
              }
            }else{
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
              return false;
            }
          }
          else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeMulti){
            if (sourcefields.length!=rule.matchValueList.length || (sourcefields.length<2 || rule.matchValueList.length<2)) {
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "Paired AND/OR is not valid unless the number of "+fieldname+" and Match Values are the same and is greater than or equal to two."),"Please change to AND or OR instead.");
              return false;
            }
          }
          else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueSingle){
            if(rule.matchValue && rule.matchValue.length!=0){
              for (const matchv of rule.matchValue) {
                if(matchv.text==""){
                  this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
                  return false;
                }
              }
            }else{
              if(rule.criteria!=Criteria.empty){
                this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
                return false;
              }
            }
          }
          else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueMulti){
            if (sourcefields.length!=rule.matchValue.length || (sourcefields.length<2 || rule.matchValue.length<2)) {
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "Paired AND/OR is not valid unless the number of "+fieldname+" and Match Values are the same and is greater than or equal to two."),"Please change to AND or OR instead.");
              return false;
            }
          }
          else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiSingle){
            if(rule.matchValue && rule.matchValue.length!=0){
              for (const matchv of rule.matchValue) {
                if(matchv.text==""){
                  this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
                  return false;
                }
              }
            }
            else{
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
              return false;
            }
          }
          else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiMulti){
            if (sourcefields.length!=rule.matchValue.length || (sourcefields.length<2 || rule.matchValue.length<2)) {
              this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "Paired AND/OR is not valid unless the number of "+fieldname+" and Match Values are the same and is greater than or equal to two."),"Please change to AND or OR instead.");
              return false;
            }
            for (const matchv of rule.matchValue) {
              if(matchv.text==""){
                this.displayWarning(TransformRuleViewModel.ruleMessage(rule, "matchValue can not be blank."),"Please fix the validation error.");
                return false;
              }
            }
          }
        }
      }
      await this.showRuleWarnings(warnings);
      return true;
    }
    async onRunTransformValidate() {
      var valid= await this.validateBeforeRunSave();
      if (!valid) {
        return;
      }
      this.onRunTransform();
    }
    onAndOrChange(rule: ColumnCleanerRuleViewModel) {
      if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeSingle){
        // if(rule.fields.length>1){
        //   rule.fields = [rule.fields[0]];
        // }
        // if(rule.matchValueList.length>1){
        //   rule.matchValueList=[rule.matchValueList[0]];
        // }
      }else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueSingle){
        if(rule.criteria==Criteria.lastnDays){
          if(rule.matchValue.length>1){
          rule.matchValue=[rule.matchValue[0]];
        }
        }
        // if(rule.fields.length>1){
        //   rule.fields = [rule.fields[0]];
        // }
        // if(rule.matchValue.length>1){
        //   rule.matchValue=[rule.matchValue[0]];
        // }
      }
      // else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiSingle){
      //   if(rule.fields.length>1){
      //     rule.fields = [rule.fields[0]];
      //   }
      // }
      else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeMulti){
        //same the length
        if(rule.fields.length !== rule.matchValueList.length){
          let colorIndex = rule.matchValueList.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValueList.length < rule.fields.length) {
            rule.matchValueList.push(new ColoredTextFindReplaceItem("0","1", NamedColorList[colorIndex++]))
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValueList.length > rule.fields.length) {
            rule.matchValueList.pop();
          }
        }
      }else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueMulti){
        //same the length
        if(rule.fields.length !== rule.matchValue.length){
          let colorIndex = rule.matchValue.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValue.length < rule.fields.length) {
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValue.length > rule.fields.length) {
            rule.matchValue.pop();
          }
        }
      }
      else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiMulti){
        //same the length
        if(rule.fields.length !== rule.matchValue.length){
          let colorIndex = rule.matchValue.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValue.length < rule.fields.length) {
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValue.length > rule.fields.length) {
            rule.matchValue.pop();
          }
        }
      }
    }
    onAndOrChangeAdv(rule: ColumnCleanerRuleViewModel) {
      if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeSingle){
        // if(rule.fields.length>1){
        //   rule.fields = [rule.fields[0]];
        // }
        // if(rule.matchValueList.length>1){
        //   rule.matchValueList=[rule.matchValueList[0]];
        // }
      }else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueSingle){
        if(rule.criteria==Criteria.lastnDays){
          if(rule.matchValue.length>1){
          rule.matchValue=[rule.matchValue[0]];
        }
        }
        // if(rule.conditionalFields.length>1){
        //   rule.conditionalFields = [rule.conditionalFields[0]];
        // }
        // if(rule.matchValue.length>1){
        //   rule.matchValue=[rule.matchValue[0]];
        // }
      }
      // else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiSingle){
      //   if(rule.conditionalFields.length>1){
      //     rule.conditionalFields = [rule.conditionalFields[0]];
      //   }
      // }
      else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.rangeMulti){
        //same the length
        if(rule.conditionalFields.length !== rule.matchValueList.length){
          let colorIndex = rule.matchValueList.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValueList.length < rule.conditionalFields.length) {
            rule.matchValueList.push(new ColoredTextFindReplaceItem("0","1", NamedColorList[colorIndex++]))
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValueList.length > rule.conditionalFields.length) {
            rule.matchValueList.pop();
          }
        }
      }else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.valueMulti){
        //same the length
        if(rule.conditionalFields.length !== rule.matchValue.length){
          let colorIndex = rule.matchValue.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValue.length < rule.conditionalFields.length) {
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValue.length > rule.conditionalFields.length) {
            rule.matchValue.pop();
          }
        }
      }
      else if(this.matchValueEntryService.MatchValueEntryEditor(rule.criteria,rule.andOr)==MatchValueEntryType.multiMulti){
        //same the length
        if(rule.conditionalFields.length !== rule.matchValue.length){
          let colorIndex = rule.matchValue.length;
          // If rule.matchValue is shorter, add blank items
          while (rule.matchValue.length < rule.conditionalFields.length) {
            rule.matchValue.push(new ColoredTextItem("", NamedColorList[colorIndex++]));
          }

          // If rule.matchValue is longer, remove excess items
          while (rule.matchValue.length > rule.conditionalFields.length) {
            rule.matchValue.pop();
          }
        }
      }
    }

    getDataTypeCriteria(criteria: Criteria): DataTypeCriteria {
      if([
        Criteria.boolTrue,
        Criteria.boolFalse].includes(criteria)){
        return DataTypeCriteria.bool;
      }else if([
        Criteria.equals,
        Criteria.lessThan,
        Criteria.greaterThan,
        Criteria.LessThanOrEqualTo,
        Criteria.greaterThanOrEqualTo,
        Criteria.between,
        Criteria.inclusiveLeft,
        Criteria.inclusiveRight,
        Criteria.inclusiveBoth].includes(criteria)){
        return DataTypeCriteria.number;
      }else if([
        Criteria.lastnDays,
        Criteria.equalToday].includes(criteria)){
        return DataTypeCriteria.timestamp;
      }
      // else if([
      //   Criteria.lastnDays,
      //   Criteria.equalToday].includes(criteria)){
      //   return DataTypeCriteria.string;
      // }
      else{
        return DataTypeCriteria.all;
      }
    }

    onToggleResetRows(event: any){
      this.viewModel.resetRows=event.checked;
    }
    onChangeType(rule:ColumnCleanerRuleViewModel){
        if(rule.duplicatesType===DuplicateType.exclude){
          var flist=rule.fields.find(fl=>fl.text=="All");
          if(flist){
            rule.fields=[];
            rule.fields.push(new ColoredTextItem("None",NamedColorList[0]));
          }
        }else if(rule.duplicatesType===DuplicateType.include){
          var flist=rule.fields.find(fl=>fl.text=="None");
          if(flist){
            rule.fields=[];
            rule.fields.push(new ColoredTextItem("All",NamedColorList[0]));
          }
        }
      }

    

  async onMoveColumnsCellClicked(rule: ColumnCleanerRuleViewModel): Promise<void> {
    let title: string='Source to Target Field Mapping ('+RuleTypeUtil.toString(rule.ruleType)+')';
    var labelFind: string ="Source Field";
    var labelReplace: string ="Target Field";
    var placeholderFind: string ="Enter Source";
    var placeholderReplace: string ="Enter Target";
    let fieldType: string="autocomplete";
    let isDuplicateRemoved: boolean=true;
    let canAddNewAutocomplete: boolean=false;
    let optionsFind: OptionItemDataType[]=[];
    let optionsReplace: OptionItemDataType[]=[];
    let validateboth: boolean=false;
    let isDataTypeCheck: boolean=true;
    let dialogWidth: string='650px';
    var optionsSource: OptionItemDataType[] = [];//Option values for 'dropdown'and 'autocomplete'
    var optionsTarget: OptionItemDataType[] = [];//Option values for 'dropdown'and 'autocomplete'
    optionsSource=await this.getPreviewTableOptionItemNormalizedDataType(this.inputDataFarmPreviewTable);
    const selectedNode=this.userData.getSelectedNodeDetails();
    optionsTarget=await this.getDataDictOptionItemNormalizedDataType(selectedNode.farmtypeid);
    //remove row
    optionsFind=optionsSource.filter(item=>item.text!="row").sort((a, b) => a.text.localeCompare(b.text));
    optionsReplace=optionsTarget.filter(item=>item.text!="row").sort((a, b) => a.text.localeCompare(b.text));

    this.showFindReplaceEditorDialogOption(title,labelFind,labelReplace,placeholderFind,placeholderReplace,fieldType,isDuplicateRemoved,canAddNewAutocomplete,optionsFind,optionsReplace,validateboth,isDataTypeCheck,dialogWidth,
      [...rule.moveColumns],
      async (items: ColoredTextFindReplaceItem[],eventname:string) => {
        if(eventname=='save'){
        rule.moveColumns = items;
        }
      });
  }
  
  async setDefaultValues(transformType:TransformType): Promise<void>{
    const selectedNode=this.userData.getSelectedNodeDetails();
    const masterconfig=this.userData.getMasterTransformers().find(x=>x.id==transformType);
    var parameters=masterconfig.parameters.find(x=>x.farmtypeid==selectedNode.farmtypeid);
    if(parameters){
      var params=parameters.parameters;
      //other parameters
        this.subTransformType=params.find(x=>x.jsonobject=='subTransformType');
        //advanced settings parameters
        this.threads=params.find(x=>x.jsonobject=='threads');
        this.resetRows=params.find(x=>x.jsonobject=='resetRows');
        this.findWildCard=params.find(x=>x.jsonobject=='findWildCard');

        this.viewModel.subTransformType=Number(this.subTransformType.default);
      //check this is first this.viewModel.sampleEnd =0
      if((this.viewModel.sampleEnd-1)==0 || isNaN(this.viewModel.sampleEnd)){
        if(this.threads.default!=""){
          this.viewModel.threads=Number(this.threads.default);
        }
        if(this.findWildCard.default!=""){
          this.viewModel.findWildCard=this.findWildCard.default;
        }
        if(this.resetRows.default!=""){
          this.viewModel.resetRows=this.stringToBoolean(this.resetRows.default);
        }
      }
    }else{
      //advanced settings parameters
      // this.bufferRows.label="Buffer Rows";this.bufferRows.jsonobject="bufferRows";this.bufferRows.min=0;this.bufferRows.max=10000000;
      // this.threads.label="Threads (0 > Auto)";this.threads.jsonobject="threads";this.threads.min=0;this.threads.max=64;
      // this.resetRows.label="Reset Row Numbers";this.resetRows.jsonobject="resetRows";
    }
  }
  async removeInvalidValues(): Promise<void>{
    //other parameters
    //advanced settings parameters
  }
  async loadImportTransformer(transform: any): Promise<void>{
    const busyIndicator = this.showBusyDialog('Please wait...');
    try{
      if(this.userData.getSelectedNodeDetails().farmtypeid!=transform.farmType){
        this.displayError("Current DataFarmType is " + DataFarmTypeUtil.toString(this.userData.getSelectedNodeDetails().farmtypeid) + ". But you are trying to load a different DataFarmType: " + DataFarmTypeUtil.toString(transform.farmType),"Error");
        return;
      }else if(this.transformType!=transform.transformType){
        this.displayError("Current transform is " + TransformTypeUtil.toString(this.transformType) + ". But you are trying to load a different transform type: " + TransformTypeUtil.toString(transform.transformType),"Error");
        return;
      }
      var ctranformconfig=this.dataTransformConfigViewModel.transforms.find(x=>x.guid=== this.userData.getSelectedTransform().guid);
      ctranformconfig.transform.transformConfig=transform;
      var newmodel =new ColumnCleanerViewModel(this.dataDictService,this.configService,this.dataTransformConfigViewModel,ctranformconfig,this.matchValueEntryService);
      // force new object reference for UI refresh
      this.viewModel.rules=[];
      this.viewModel.rules=[...newmodel.rules];
      this.isRuleSetEmpty = this.viewModel.rules.length === 0;
      //this.dataSource.data = this.viewModel.rules;
      this.prepareRuleData();
      this.viewModel.reindexRules();
      this.cdRef.detectChanges();
      //update Parameters
      this.viewModel.parameters.sortInfo=newmodel.parameters.sortInfo;
      this.viewModel.parameters.includeInResults=newmodel.parameters.includeInResults;
      this.viewModel.parameters.sampleStart =newmodel.parameters.sampleStart;
      this.viewModel.parameters.sampleEnd =newmodel.parameters.sampleEnd;
      this.viewModel.parameters.runAll =newmodel.parameters.runAll;
      //other parameters
      this.viewModel.threads = newmodel.threads;
      this.viewModel.resetRows = newmodel.resetRows;
      this.viewModel.findWildCard = newmodel.findWildCard;
      //advanced settings parameters
      
      //update rule table height
      
    }catch (err) {
      this.displayError(`Failed to save import transformer`, 'Error', err);
    }finally {
      busyIndicator.close();
    }
  }
}
export enum DataTypeCriteria {
  all = 1,
  bool,
  timestamp,
  string,
  number,
}

